import axios, { AxiosInstance } from 'axios'

// Base API URL - uses environment variable with fallback to /api
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'

class ApiClient {
  private client: AxiosInstance
  private getAccessToken: (() => Promise<string>) | null = null
  private onAuthFailure: (() => void) | null = null

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor for adding auth token and dealership context
    this.client.interceptors.request.use(
      async config => {
        // Use the signed-in session token if available
        if (this.getAccessToken) {
          try {
            const token = await this.getAccessToken()
            config.headers.Authorization = `Bearer ${token}`
          } catch (error) {
            console.error('Failed to get access token:', error)
            // Fallback to localStorage token for backward compatibility
            const fallbackToken = localStorage.getItem('auth_token')
            if (fallbackToken) {
              config.headers.Authorization = `Bearer ${fallbackToken}`
            }
          }
        } else {
          // Fallback to localStorage token
          const token = localStorage.getItem('auth_token')
          if (token) {
            config.headers.Authorization = `Bearer ${token}`
          }
        }

        // Add selected dealership to requests
        const selectedDealershipId = localStorage.getItem('selectedDealershipId')
        if (selectedDealershipId && selectedDealershipId !== 'all') {
          config.headers['X-Dealership-Id'] = selectedDealershipId
        }

        return config
      },
      error => {
        return Promise.reject(error)
      },
    )

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      response => response,
      error => {
        if (error.response?.status === 401) {
          localStorage.removeItem('auth_token')
          this.onAuthFailure?.()
        }
        return Promise.reject(error)
      },
    )
  }

  setAuthTokenGetter(getAccessToken: () => Promise<string>) {
    this.getAccessToken = getAccessToken
  }

  clearAuthTokenGetter() {
    this.getAccessToken = null
  }

  setOnAuthFailure(handler: () => void) {
    this.onAuthFailure = handler
  }

  clearOnAuthFailure() {
    this.onAuthFailure = null
  }

  // Generic methods
  async get<T>(url: string, params?: any): Promise<T> {
    const response = await this.client.get<T>(url, { params })
    return response.data
  }

  async post<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.post<T>(url, data)
    return response.data
  }

  async put<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.put<T>(url, data)
    return response.data
  }

  async patch<T>(url: string, data?: any): Promise<T> {
    const response = await this.client.patch<T>(url, data)
    return response.data
  }

  async delete<T>(url: string): Promise<T> {
    const response = await this.client.delete<T>(url)
    return response.data
  }
}

export const apiClient = new ApiClient()
